import {
  CarrierSubProgramType,
  ProgramEnrolmentType,
  SubProgramEnrolmentType,
  ThirdPartyEdiServiceType,
  ThirdPartySubProgramType,
  WarehouseSubProgramType,
} from '@carm/_core/program-enrolment/models/enrol-program-enums.model';
import { IKeyValue } from '@carm/_utils/models';
import { DocumentGroups, IDocumentTypesList, IUploadedDocument } from '.';
import { IUploadedDocumentApiResponse } from './document-types-list.model';
import { acceptedFiles } from '@carm/shared/constants/const-file-uploads-extensions';
import { FilesizePipe } from '@carm/shared/pipes/file-size.pipe';
import * as dayjs from 'dayjs';

export const getDocumentGroupByType = (
    type: ProgramEnrolmentType, subType?: WarehouseSubProgramType
    | ThirdPartyEdiServiceType | SubProgramEnrolmentType | CarrierSubProgramType | ThirdPartySubProgramType)
  : DocumentGroups => {
    if (!!subType) {
      return documentGroupToSubProgramMap.get(`${type}_${subType}`);
    }
    switch (type) {
      case ProgramEnrolmentType.CustomsBroker:
        return DocumentGroups.CustomsBroker;
      case ProgramEnrolmentType.DutyFreeShop:
        return DocumentGroups.DutyFreeShop;
      case ProgramEnrolmentType.Exporter:
        return DocumentGroups.Exporter;
      case ProgramEnrolmentType.Importer:
        return DocumentGroups.Importer;
      case ProgramEnrolmentType.CarrierFreightForwarder:
        return DocumentGroups.Carrier;
      case ProgramEnrolmentType.ThirdParty:
        return DocumentGroups.ThirdParty;
    }
};

const documentGroupToSubProgramMap = new Map<string, DocumentGroups>([
  [`${ProgramEnrolmentType.Warehouse}_${WarehouseSubProgramType.CustomsBondedWarehouse}`, DocumentGroups.CustomsBondedWarehouse],
  [`${ProgramEnrolmentType.Warehouse}_${WarehouseSubProgramType.SufferanceWarehouse}`, DocumentGroups.SufferanceWarehouse],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.AcceleratedCommercialReleaseOperations}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.AdvanceCommercialInformation}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.CommercialAccountingDeclaration}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.DailyNoticeAndStatementOfAccount}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.G7Edi}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.MarineExportReporting}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.ReleaseNotificationSystem}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${ThirdPartyEdiServiceType.IntegratedImportDeclaration}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.ThirdParty}_${SubProgramEnrolmentType.Edi}`, DocumentGroups.Edi],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.Edi}`, DocumentGroups.ImporterEdi],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.CandianGoodsAbroad}`, DocumentGroups.ImporterCanadianGoodsAbroad],
  // [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.CoastingTradeLicence}`, DocumentGroups.ImporterCoastingTradeLicence],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.ContainerBank}`, DocumentGroups.ImporterContainerBank],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.DutyReliefLicence}`, DocumentGroups.ImporterDuties],
  // [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.DutyRemission}`, DocumentGroups.ImporterDutiesRemission],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.DutyRemissionOn120Basis}`, DocumentGroups.ImporterDutiesRemission120],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.ReleasePriorPayment}`, DocumentGroups.ImporterRpp],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.TrustedTrader}`, DocumentGroups.ImporterTrustedTrader],
  [`${ProgramEnrolmentType.Importer}_${SubProgramEnrolmentType.ReportingSummaryAccounting}`, DocumentGroups.CarrierSummary],
  [`${ProgramEnrolmentType.Exporter}_${SubProgramEnrolmentType.Edi}`, DocumentGroups.ExporterEdi],
  [`${ProgramEnrolmentType.CustomsBroker}_${SubProgramEnrolmentType.Edi}`, DocumentGroups.CustomsBrokerEdi],
  [`${ProgramEnrolmentType.Warehouse}_${SubProgramEnrolmentType.Edi}`, DocumentGroups.WarehouseSufferanceEdi],
  [`${ProgramEnrolmentType.CarrierFreightForwarder}_${SubProgramEnrolmentType.Edi}`, DocumentGroups.CarrierEdi],
  [`${ProgramEnrolmentType.CarrierFreightForwarder}_${SubProgramEnrolmentType.Bonded}`, DocumentGroups.CarrierBonded],
  [`${ProgramEnrolmentType.CarrierFreightForwarder}_${SubProgramEnrolmentType.TrustedTrader}`, DocumentGroups.CarrierCsa],
  [`${ProgramEnrolmentType.CarrierFreightForwarder}_${SubProgramEnrolmentType.ReportingSummaryAccounting}`, DocumentGroups.CarrierSummary],
]);

export const mapComplianceTypeToDocumentGroup = new Map<string, DocumentGroups> ([
  ['trade', DocumentGroups.TRADE_COMPLIANCE],
  ['program', DocumentGroups.PROGRAM_COMPLIANCE],
  ['rpp', DocumentGroups.PROGRAM_COMPLIANCE_RPP],
  ['sima', DocumentGroups.SIMA_COMPLIANCE],
]);

export const mapToKeyValue = (data: IDocumentTypesList, localeId: string): IKeyValue[] => {
  return data?.documentTypeList?.map((docType) => {
    return {
      key: docType,
      value: mapToLocalizedValue(docType, localeId),
    } as IKeyValue;
  });
};

export const mapToUploadedDocuments = (documentInfo: IUploadedDocumentApiResponse, localeId: string): IUploadedDocument => {
  const document = extractInfoFromFilename(documentInfo.name, documentInfo.documentType);
  return {
    uid: documentInfo?.id,
    filename: mapToLocalizedValue(document?.name, localeId),
    fileSize: documentInfo?.formattedSize,
    fileType: mapToLocalizedValue(documentInfo?.documentType, localeId),
    createDate: documentInfo?.createDate ? dayjs(documentInfo?.createDate).format('YYYY-MM-DD') : null,
    createTime: documentInfo?.createDate ? dayjs(documentInfo?.createDate).format('HH:mm:ss') : null,
    createDateTime: documentInfo?.createDate,
    mediaType: documentInfo?.mediaType,
    version: documentInfo?.cadVersion,
    fileExtension: documentInfo?.fileExtension
  } as IUploadedDocument;
};

export const mapToLocalizedValue = (value: string, localeId: string): string => {

  // fragile name parsing. Should be avoided... filename comes from Opentext so limited options
  const values = value?.split('|');
  const isLocalized = values?.length > 1;

  if(isLocalized && localeId === 'en'){
    return values[0]?.trim();
  } else if(isLocalized && localeId === 'fr') {

    let tempFr = values[1];
    const tempFrNoDate = tempFr.split('-')[0].trim();
    return tempFrNoDate;
  }else {
    return value;
  }
};

export const mapFilesToUploadedDocuments = (
  files: {filename: string, fileSize: number, fileType: string}[], localeId: string, fileSizePipe: FilesizePipe): IUploadedDocument[] => {
  return files.map(fileInfo => {
    const file = extractInfoFromFilename(fileInfo.filename, fileInfo.fileType);
    return {
      filename: file.name,
      fileSize: fileSizePipe.transform(fileInfo.fileSize),
      fileType: mapToLocalizedValue(fileInfo.fileType, localeId),
      createDate: file.dateUploaded,
      createTime: file.timeUploaded
    } as IUploadedDocument;
  });
};

/**
 * filename may come with timestamp appended to the filename, extract it to display separately and remove from filename
 * (filename may not have timestamp on it, for all sorts of reason)
 * ex: filename from OpenText: 'CARM-230320-13026-2020-03-30-17-55-00-588.pdf'
 * timestamp: 2020-03-30-17-55-00-588
 * extract this info:
 * date uploaded to : 2020-03-30
 * time uploaded: 17:55
 * name: CARM-230320-13026.pdf
 * @param filename
 * @param documentType
 */
export const extractInfoFromFilename = (filename: string, documentType: string):
    { dateUploaded: string; timeUploaded: string; name: string, type: string } => {

    let dateUploaded = '';
    let timeUploaded = '';
    let name = filename;
    let type = documentType;

    if (documentType) {
      const enFr = documentType.split('|');
      if (enFr && enFr.length > 0) {
          type = enFr[0];
      }
    }

    const af = acceptedFiles.join('|');
    const dateExp = `-\\d{4}(-\\d{2}){5}-\\d{1,3}(${af})$`;
    const dateRegexMatch = new RegExp(dateExp, 'gi');
    const dateMatch = filename.match(dateRegexMatch);

    if (dateMatch) {
        dateUploaded = dateMatch[0].substr(1, 10);
        timeUploaded = dateMatch[0].substr(12, 8).replace(/-/g, ':');

        // filename without date
        const fileExt = filename.substr(filename.lastIndexOf('.'));
        name = filename.replace(dateMatch[0], fileExt);

        return { dateUploaded, timeUploaded, name, type };
    }

    const timeStampExp = `-\\d{13}(${af})$`;
    const timeStampRegexMatch = new RegExp(timeStampExp, 'gi');
    const timeStampMatch = filename.match(timeStampRegexMatch);

    if (timeStampMatch) {
      const fileExt = filename.substr(filename.lastIndexOf('.'));
      name = filename.replace(timeStampMatch[0], fileExt);
    }

    return { dateUploaded, timeUploaded, name, type };
};
